import { UserCheck, Clock, FileText, CheckCircle, XCircle, Users, Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Badge } from "@/app/_module/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";

interface Assignment {
  id: string;
  leadName: string;
  agentName: string;
  agentAgency?: string;
  assignedAt: string;
  status: "active" | "completed" | "cancelled" | "unassigned";
  notes: string;
  isHouseholdPrimary?: boolean;
  householdMemberCount?: number;
}

interface AssignmentHistoryProps {
  assignments: Assignment[];
}

const STATUS_CHIPS: { key: string; label: string }[] = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "completed", label: "Completed" },
  { key: "cancelled", label: "Cancelled" },
  { key: "unassigned", label: "Unassigned" },
];

const dayLabel = (dateString: string) => {
  const d = new Date(dateString);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(d, today)) return "Today";
  if (sameDay(d, yesterday)) return "Yesterday";
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
};

export const AssignmentHistoryComponent = function ({ assignments }: AssignmentHistoryProps) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [visibleCount, setVisibleCount] = useState(30);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <UserCheck className="h-4 w-4 text-primary" />;
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "cancelled":
        return <XCircle className="h-4 w-4 text-red-600" />;
      case "unassigned":
        return <XCircle className="h-4 w-4 text-gray-600" />;
      default:
        return <Clock className="h-4 w-4 text-gray-600" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "active":
        return "bg-primary/10 text-primary border-primary/30";
      case "completed":
        return "bg-green-100 text-green-800 border-green-200";
      case "cancelled":
        return "bg-red-100 text-red-800 border-red-200";
      case "unassigned":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const formatTime = (dateString: string) =>
    new Date(dateString).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: assignments.length };
    for (const a of assignments) counts[a.status] = (counts[a.status] || 0) + 1;
    return counts;
  }, [assignments]);

  // Newest first: day headers need time order. The list arrives status-first
  // (active, completed, unassigned), which split one day into several groups
  // sharing a key; the status chips already cover grouping by status.
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return assignments.filter((a) => {
      if (statusFilter !== "all" && a.status !== statusFilter) return false;
      if (!q) return true;
      return (
        (a.leadName || "").toLowerCase().includes(q) ||
        (a.agentName || "").toLowerCase().includes(q) ||
        (a.agentAgency || "").toLowerCase().includes(q) ||
        (a.notes || "").toLowerCase().includes(q)
      );
    }).sort((a, b) => (new Date(b.assignedAt).getTime() || 0) - (new Date(a.assignedAt).getTime() || 0));
  }, [assignments, search, statusFilter]);

  const grouped = useMemo(() => {
    const groups: { day: string; items: Assignment[] }[] = [];
    for (const a of filtered.slice(0, visibleCount)) {
      const day = dayLabel(a.assignedAt);
      const last = groups[groups.length - 1];
      if (last && last.day === day) last.items.push(a);
      else groups.push({ day, items: [a] });
    }
    return groups;
  }, [filtered, visibleCount]);

  return (
    <Card className="flex flex-col h-[calc(100vh-220px)] min-h-[480px]">
      <CardHeader className="flex-shrink-0 space-y-2.5 pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-lg font-semibold">
            Assignment History
            <span className="ml-2 text-sm font-normal text-gray-400">{filtered.length}</span>
          </CardTitle>
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => { setSearch(e.target.value); setVisibleCount(30); }}
              placeholder="Search history..."
              className="h-8 w-[180px] pl-8 text-xs"
            />
          </div>
        </div>
        {/* Status filter chips with live counts */}
        <div className="flex flex-wrap gap-1.5">
          {STATUS_CHIPS.filter((c) => c.key === "all" || (statusCounts[c.key] || 0) > 0).map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={() => { setStatusFilter(chip.key); setVisibleCount(30); }}
              className={`px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                statusFilter === chip.key
                  ? "bg-primary text-white border-primary"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {chip.label}
              <span className={`ml-1 tabular-nums ${statusFilter === chip.key ? "opacity-80" : "text-gray-400"}`}>
                {statusCounts[chip.key] || 0}
              </span>
            </button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-y-auto">
        {filtered.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>{assignments.length === 0 ? "No assignment history" : "Nothing matches your filters"}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {grouped.map((group) => (
              <div key={group.day}>
                <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-white/95 backdrop-blur-sm">
                  <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{group.day}</span>
                </div>
                <div className="space-y-3 mt-1.5">
                  {group.items.map((assignment) => (
                    <div
                      key={assignment.id}
                      className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {getStatusIcon(assignment.status)}
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-gray-900">{assignment.leadName}</h3>
                              {assignment.isHouseholdPrimary && (
                                <Badge className="text-xs bg-purple-100 text-purple-800 border-purple-200 flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {assignment.householdMemberCount && assignment.householdMemberCount > 0
                                    ? `+${assignment.householdMemberCount} members`
                                    : "Household"}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600">
                              Assigned to {assignment.agentName}
                              {assignment.agentAgency && <span className="text-xs text-gray-400"> · {assignment.agentAgency}</span>}
                            </p>
                          </div>
                        </div>

                        <Badge className={`text-xs ${getStatusColor(assignment.status)}`}>
                          {assignment.status}
                        </Badge>
                      </div>

                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Clock className="h-3 w-3" />
                          <span title={new Date(assignment.assignedAt).toLocaleString()}>
                            Assigned at {formatTime(assignment.assignedAt)}
                          </span>
                        </div>

                        {assignment.notes && (
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <div className="flex items-start gap-2">
                              <FileText className="h-4 w-4 text-gray-500 mt-0.5" />
                              <p className="text-sm text-gray-700">{assignment.notes}</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {filtered.length > visibleCount && (
              <div className="pt-1 pb-2 text-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + 30)}
                  className="px-4 py-1.5 rounded-full text-xs font-medium border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Load more ({filtered.length - visibleCount} older)
                </button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
